import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Loader2,
  ChevronsLeft,
  ChevronsRight,
  Menu,
  X,
  ChevronLeft,
  Gift,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { usePermissions } from '../context/PermissionContext';
import { useSidebar } from '../context/SidebarContext';
import { supabase } from '../lib/supabase';
import { retrySupabase } from '../utils/retry';
import { mergeLastCrearAnuncioState } from '../utils/crearAnuncioStorage';

// Sidebar aplanada: solo pestañas vigentes.
// Accesos en desuso comentados y se indica la categoría original.
// Categorías en desuso (antigua versión):
//  - Ventas (Registro de ventas, Historial de ventas, Mensajes, Clientes, Gráficos de Interacción, Envíos)
//  - Inventario (Lista de productos, Stock, Descuentos)
//  - Compras (Registro de compras, Historial de compras, Forecasting, Proveedores)
//  - Finanzas (Flujo de caja, Forecasting financiero, Gastos recurrentes)

type FlatMenuItem = {
  name: string;
  icon: React.ReactNode;
  path: string;
  permissionKey: string; // clave en PermissionContext
  // categoría original (solo para referencia, no visible)
  category: 'Generación de imágenes' | 'Empleado IA';
  devOnly?: boolean;
};

  // Pestañas vigentes (sin separación visible). Comentado en qué categoría estaban.
  const FLAT_MENU_ITEMS: FlatMenuItem[] = [
    // Generación de imágenes
    { name: 'Estudio Virtual', icon: <span className="material-symbols-rounded">image</span>, path: '/redes-sociales/crear-anuncio', permissionKey: 'crear-anuncio', category: 'Generación de imágenes' },
    { name: 'Test V1', icon: <span className="material-symbols-rounded">smart_toy</span>, path: '/test-v1', permissionKey: 'test-v1', category: 'Generación de imágenes', devOnly: true },
    { name: 'Historial', icon: <span className="material-symbols-rounded">schedule</span>, path: '/redes-sociales/imagenes', permissionKey: 'imagenes', category: 'Generación de imágenes' },
    // Empleado IA
    { name: 'Manager de Redes IA', icon: <span className="material-symbols-rounded">smart_toy</span>, path: '/redes-sociales/calendario', permissionKey: 'calendario', category: 'Empleado IA', devOnly: true },
  ];

const PLAN_LABELS: Record<string, string> = {
  gratis: 'Gratis',
  emprendimiento: 'Básico',
  negocio: 'Pro',
};

interface UserProfile {
  id: string;
  name: string;
  email: string;
  id_company: string | null;
  company_name?: string | null;
  company_logo?: string | null;
  dev_tester?: boolean | null;
  plan?: string | null;
  role: string | null;
  max_fotos_mes?: number | null;
  imagenes_restantes?: number | null;
  pais?: string | null;
}

export const Sidebar = () => {
  const location = useLocation();
  const { isCollapsed, toggleCollapsed, mobileTitleOverride, mobileBackOverride, mobileHideBack } = useSidebar();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(() => {
    try {
      const cached = localStorage.getItem('atlas-user-plan');
      if (cached) return { plan: cached } as UserProfile;
    } catch {}
    return null;
  });
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // OLD: mobileNavWidth and panelRef were for the slide-out panel — kept commented for reference
  // const [mobileNavWidth, setMobileNavWidth] = useState<number>(0);
  // const panelRef = useRef<HTMLDivElement | null>(null);

  // Clear Crear flow selected products when navigating via navbar/sidebar
  const clearCrearSelection = React.useCallback(() => {
    try {
      const isInCrearFlow = location.pathname.startsWith('/redes-sociales/crear-anuncio');
      if (isInCrearFlow) {
        mergeLastCrearAnuncioState({ selectedVariantIds: [] });
      }
    } catch {
      // noop
    }
  }, [location.pathname]);

  // Get user permissions
  const { permissions } = usePermissions();
  
  // Siempre remover espaciado lateral de sidebar (ya no se usa en desktop)
  useEffect(() => {
    document.documentElement.classList.add('no-sidebar-spacing');
    document.body.classList.add('no-sidebar-spacing');
    return () => {
      // mantener la clase para todo el app
      document.documentElement.classList.add('no-sidebar-spacing');
      document.body.classList.add('no-sidebar-spacing');
    };
  }, []);
  
  // OLD: Compute dynamic mobile nav width — no longer needed with dropdown menu
  // useEffect(() => { ... setMobileNavWidth(width); ... }, []);
  
  // Sync internal state when other components dispatch the same toggle event
  useEffect(() => {
    const handler = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail as any;
        if (typeof detail?.isMobileMenuOpen === 'boolean') {
          setMobileMenuOpen(detail.isMobileMenuOpen);
        }
      } catch {
        // noop
      }
    };
    window.addEventListener('sidebar-mobile-toggle' as any, handler as EventListener);
    return () => window.removeEventListener('sidebar-mobile-toggle' as any, handler as EventListener);
  }, []);

  // OLD: Apply/Remove inert on slide-out panel — no longer needed
  // useEffect(() => { panelRef.current... }, [mobileMenuOpen]);
  
  // Fetch user profile including company name
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile, error } = await retrySupabase(async () =>
          await supabase
            .from('profiles')
            .select('id, name, email, id_company, role')
            .eq('id', user.id)
            .single()
        );

        if (error) throw error;
        
        // If user has a company, fetch the company name and logo from company_profiles table
        if (profile.id_company) {
          const { data: companyData, error: companyError } = await retrySupabase(async () =>
            await supabase
              .from('company_profiles')
              .select('nombre, logo_url, dev_tester, plan, max_fotos_mes, imagenes_restantes, pais')
              .eq('id_company', profile.id_company)
              .single()
          );
            
          if (!companyError && companyData) {
            // Add the company name and logo to the profile object from company_profiles
            const profileWithCompanyInfo = {
              ...profile,
              company_name: companyData.nombre,
              company_logo: companyData.logo_url,
              dev_tester: companyData.dev_tester,
              plan: companyData.plan,
              max_fotos_mes: companyData.max_fotos_mes,
              imagenes_restantes: companyData.imagenes_restantes,
              pais: companyData.pais,
            };
            setUserProfile(profileWithCompanyInfo);
            try { localStorage.setItem('atlas-user-plan', companyData.plan || 'gratis'); } catch {}
            return; // Exit early since we've already set the profile
          }
        }
        
        // If we get here, we either don't have a company or couldn't fetch company data
        setUserProfile(profile);
      } catch (err) {
        console.error('Error fetching user profile:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  // Se removió el manejo de secciones/categorías para aplanar la navegación

  // Toggle function removed; App.tsx controls mobile menu and dispatches the event.

  // Close mobile menu when a link is clicked
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    window.dispatchEvent(new CustomEvent('sidebar-mobile-toggle', {
      detail: { isMobileMenuOpen: false }
    }));
  };

  // Mobile nav selection: clear Crear selection (if applicable) and close menu
  const handleMobileNavSelection = () => {
    clearCrearSelection();
    closeMobileMenu();
  };

  // Also close mobile menu when the route changes
  useEffect(() => {
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
      window.dispatchEvent(new CustomEvent('sidebar-mobile-toggle', {
        detail: { isMobileMenuOpen: false }
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Items permitidos según permisos y beta tester (para items marcados betaOnly)
  const isDevTester = userProfile?.dev_tester === true;
  const permittedFlatItems = FLAT_MENU_ITEMS.filter(item => permissions[item.permissionKey] && (!item.devOnly || isDevTester));

  // Toggle mobile menu locally (new dropdown style)
  const toggleMobileMenu = () => {
    const next = !mobileMenuOpen;
    setMobileMenuOpen(next);
    window.dispatchEvent(new CustomEvent('sidebar-mobile-toggle', {
      detail: { isMobileMenuOpen: next }
    }));
  };

  // Find the active page title for the top bar
  const activeItem = permittedFlatItems.find(item => {
    const target = item.path === '/redes-sociales/crear-anuncio'
      ? '/redes-sociales/crear-anuncio'
      : item.path;
    return location.pathname.startsWith(target);
  });
  const mobileTitle = mobileTitleOverride
    ? mobileTitleOverride
    : location.pathname.includes('/prueba-nueva-interfaz')
      ? 'Diseña tus imágenes'
      : activeItem?.name
        || (location.pathname.startsWith('/ayuda') ? 'Ayuda' : null)
        || (location.pathname.startsWith('/configuracion') ? 'Configuración' : null)
        || (location.pathname.startsWith('/redes-sociales/editar-imagen') ? 'Editar imagen' : null)
        || 'Estudio Atlas';
  const hideBackAndTitle = location.pathname === '/redes-sociales/crear-anuncio/seleccionar-productos';
  // All pages now use Apple HIG style for the mobile header
  const navigate = useNavigate();

  return (
    <>
      {/* ══════ Mobile Header (expands to show menu) ══════ */}
      <header
        className="fixed top-0 left-0 right-0 md:hidden z-50 bg-white border-b border-black/[0.06]"
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)',
        }}
      >
        {/* Title row: back | centered title | hamburger */}
        <div className="relative flex items-center h-14 px-4">
          {hideBackAndTitle ? (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-[7px] bg-[#1644EB] flex items-center justify-center shrink-0">
                <img src="/images/estudio-atlas-logo-solo-blanco.svg" alt="" className="w-4 h-4" />
              </div>
              <img src="/images/estudio-atlas-nombre-solo.svg" alt="Estudio Atlas" className="h-4 object-contain" />
            </div>
          ) : (
            <>
              {!mobileHideBack && (
                <button
                  onClick={() => mobileBackOverride ? mobileBackOverride() : window.history.back()}
                  className="inline-flex items-center justify-center shrink-0 text-[#1644EB] active:opacity-50 transition-opacity"
                  aria-label="Volver"
                >
                  <ChevronLeft size={22} />
                </button>
              )}
              <span className="absolute left-1/2 -translate-x-1/2 truncate max-w-[60%] text-center text-[15px] font-semibold text-brand-black/90">{mobileTitle}</span>
            </>
          )}
          <button
            onClick={toggleMobileMenu}
            className="ml-auto inline-flex items-center justify-center w-9 h-9 rounded-md transition-colors shrink-0 text-brand-black/60 active:bg-black/[0.04]"
            aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
          >
            <Menu size={20} className={`transition-transform duration-300 ${mobileMenuOpen ? 'rotate-90 opacity-0 absolute' : 'rotate-0 opacity-100'}`} />
            <X size={20} className={`transition-transform duration-300 ${mobileMenuOpen ? 'rotate-0 opacity-100' : '-rotate-90 opacity-0 absolute'}`} />
          </button>
        </div>

      </header>

      {/* ══════ Floating popover menu ══════ */}
      <div
        className={`fixed right-4 w-[272px] md:hidden z-[51] origin-top-right transition-all duration-200 ease-out ${
          mobileMenuOpen
            ? 'opacity-100 scale-100 pointer-events-auto'
            : 'opacity-0 scale-[0.9] pointer-events-none'
        }`}
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 56px)' }}
      >
        <div className="bg-white rounded-[14px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] overflow-hidden">

          {/* ── Top section: credits bar, upgrade CTA, workspace card ── */}
          {!loading && userProfile && (
            <div className="px-3 pt-3 pb-2.5 flex flex-col gap-2.5">

              {/* Credits bar */}
              {(() => {
                const remaining = typeof userProfile.imagenes_restantes === 'number' ? userProfile.imagenes_restantes : 0;
                const max = typeof userProfile.max_fotos_mes === 'number' ? userProfile.max_fotos_mes : 0;
                const isFree = userProfile.plan === 'gratis';
                const pct = isFree
                  ? (max > 0 ? (remaining / max) * 100 : 0)
                  : (max > 0 ? (Math.min(remaining, max) / max) * 100 : 0);
                return (
                  <NavLink
                    to="/configuracion"
                    onClick={handleMobileNavSelection}
                    className="block px-1 py-0.5"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[13px] text-brand-black/80">
                        {remaining} {isFree ? 'fotos gratuitas' : 'imágenes'} restantes
                      </span>
                    </div>
                    <div className="h-[5px] bg-[#F2F2F7] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[#1644EB] transition-all duration-300"
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                  </NavLink>
                );
              })()}

              {/* Upgrade CTA (free plan only) */}
              {userProfile.plan === 'gratis' && (
                <div className="flex items-center gap-2.5 bg-[#1644EB] rounded-[9px] px-3 h-[44px]">
                  <span className="material-symbols-rounded text-white text-[17px] shrink-0">auto_awesome</span>
                  <span className="text-[13px] text-white flex-1">Mejorá tu plan</span>
                  <NavLink
                    to="/subscription"
                    onClick={handleMobileNavSelection}
                    className="shrink-0 text-[13px] font-semibold text-[#1644EB] bg-white px-2.5 py-1 rounded-[6px] active:opacity-80 transition-opacity"
                  >
                    Ver planes
                  </NavLink>
                </div>
              )}

              {/* Workspace card */}
              <div className="flex items-center gap-2.5 bg-[#F2F2F7] rounded-[9px] px-2.5 h-[44px] cursor-pointer active:opacity-80 transition-opacity" onClick={() => { handleMobileNavSelection(); navigate('/configuracion'); }}>
                <img
                  src={userProfile.company_logo ? `${userProfile.company_logo}?width=32&height=32` : "/images/estudio-atlas-logo-solo-blanco.svg"}
                  alt="Logo"
                  className="w-8 h-8 rounded-[7px] object-cover bg-white shrink-0"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "/images/estudio-atlas-logo-solo-blanco.svg";
                  }}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] text-brand-black/90 truncate">{userProfile.name || 'Usuario'}</div>
                  {userProfile.company_name && (
                    <div className="text-[11px] text-[#3C3C43]/60 truncate">{userProfile.company_name}</div>
                  )}
                </div>
                {userProfile.plan && (
                  <span className="shrink-0 text-[11px] font-medium text-[#1644EB] bg-[#1644EB]/[0.08] px-1.5 py-0.5 rounded-full">
                    {PLAN_LABELS[userProfile.plan] || userProfile.plan}
                  </span>
                )}
              </div>

            </div>
          )}

          {/* ── Main nav items ── */}
          <div className="px-1.5 pb-0.5">
            {permittedFlatItems.map((item: FlatMenuItem) => (
              <NavLink
                key={item.name}
                to={item.path === '/redes-sociales/crear-anuncio'
                  ? '/redes-sociales/crear-anuncio/seleccionar-productos'
                  : item.path}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] transition-colors ${
                    isActive ? 'bg-[#1644EB]/[0.08] text-[#1644EB]' : 'text-brand-black/85 active:bg-black/[0.04]'
                  }`
                }
                onClick={handleMobileNavSelection}
              >
                {({ isActive }) => (
                  <>
                    <span className={`w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 [&>.material-symbols-rounded]:text-[14px] ${
                      isActive ? 'bg-[#1644EB] text-white' : 'bg-[#F2F2F7] text-[#3C3C43]/70'
                    }`}>
                      {item.icon}
                    </span>
                    <span>{item.name}</span>
                  </>
                )}
              </NavLink>
            ))}
          </div>

          {/* ── Separator ── */}
          <div className="mx-3 h-[0.5px] bg-black/[0.06]" />

          {/* ── Bottom section ── */}
          <div className="px-1.5 py-0.5">
            <NavLink
              to="/ayuda"
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] transition-colors ${
                  isActive ? 'bg-[#1644EB]/[0.08] text-[#1644EB]' : 'text-brand-black/85 active:bg-black/[0.04]'
                }`
              }
              onClick={handleMobileNavSelection}
            >
              {({ isActive }) => (
                <>
                  <span className={`w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 [&>.material-symbols-rounded]:text-[14px] ${
                    isActive ? 'bg-[#1644EB] text-white' : 'bg-[#F2F2F7] text-[#3C3C43]/70'
                  }`}>
                    <span className="material-symbols-rounded">help</span>
                  </span>
                  <span>Ayuda</span>
                </>
              )}
            </NavLink>
            <NavLink
              to="/configuracion"
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] transition-colors ${
                  isActive ? 'bg-[#1644EB]/[0.08] text-[#1644EB]' : 'text-brand-black/85 active:bg-black/[0.04]'
                }`
              }
              onClick={handleMobileNavSelection}
            >
              {({ isActive }) => (
                <>
                  <span className={`w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 [&>.material-symbols-rounded]:text-[14px] ${
                    isActive ? 'bg-[#1644EB] text-white' : 'bg-[#F2F2F7] text-[#3C3C43]/70'
                  }`}>
                    <span className="material-symbols-rounded">settings</span>
                  </span>
                  <span>Configuración</span>
                </>
              )}
            </NavLink>
          </div>

          {/* ── Separator ── */}
          <div className="mx-3 h-[0.5px] bg-black/[0.06]" />

          {/* ── Sign out ── */}
          <div className="px-1.5 py-0.5 pb-1.5">
            <button
              type="button"
              onClick={async () => {
                closeMobileMenu();
                await supabase.auth.signOut();
                navigate('/auth');
              }}
              className="flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] text-brand-black/85 active:bg-black/[0.04] transition-colors w-full"
            >
              <span className="w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 [&>.material-symbols-rounded]:text-[14px] bg-[#F2F2F7] text-[#3C3C43]/70">
                <span className="material-symbols-rounded">logout</span>
              </span>
              <span>Cerrar sesión</span>
            </button>
          </div>

        </div>
      </div>

      {/* Backdrop overlay */}
      <div
        className={`fixed inset-0 md:hidden z-40 transition-opacity duration-300 ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeMobileMenu}
        aria-hidden="true"
      />

    {/* ══════ Desktop Sidebar ══════ */}
    <aside
      className="hidden md:flex fixed z-30 flex-col bg-brand-black rounded-lg transition-[width] duration-300 ease-out"
      style={{ width: isCollapsed ? 72 : 240, top: 12, left: 12, bottom: 12 }}
    >
      {/* ── Logo area ── */}
      <div className={`flex items-center h-16 shrink-0 ${isCollapsed ? 'justify-center px-0' : 'px-5'}`}>
        <div className={`flex items-center ${isCollapsed ? '' : 'gap-2.5'}`}>
          <div className="w-8 h-8 rounded-[8px] bg-[#1644EB] flex items-center justify-center shrink-0">
            <img src="/images/estudio-atlas-logo-solo-blanco.svg" alt="" className="w-[18px] h-[18px]" />
          </div>
          {!isCollapsed && (
            <img src="/images/estudio-atlas-nombre-solo.svg" alt="Estudio Atlas" className="h-4 object-contain brightness-0 invert" />
          )}
        </div>
      </div>

      {/* ── Navigation ── */}
      <nav className="flex-1 flex flex-col gap-0.5 px-2 pt-2 pb-3 overflow-y-auto">
        {permittedFlatItems.map((item: FlatMenuItem) => (
          <NavLink
            key={item.name}
            to={item.path === '/redes-sociales/crear-anuncio'
              ? '/redes-sociales/crear-anuncio/seleccionar-productos'
              : item.path}
            className={({ isActive }) =>
              `flex items-center gap-2.5 rounded-lg transition-colors ${isCollapsed ? 'justify-center h-9 w-9 mx-auto' : 'px-2.5 h-9'} ${
                isActive
                  ? 'bg-white/[0.12] text-white'
                  : 'text-white/50 hover:bg-white/[0.07] hover:text-white/80'
              }`
            }
            title={item.name}
            onClick={clearCrearSelection}
          >
            <span className="shrink-0 [&>.material-symbols-rounded]:text-[18px]">{item.icon}</span>
            {!isCollapsed && <span className="text-[13px] whitespace-nowrap overflow-hidden">{item.name}</span>}
          </NavLink>
        ))}

        {/* Divider */}
        <div className={`my-1.5 h-px bg-white/[0.08] ${isCollapsed ? 'mx-1.5' : 'mx-1'}`} />

        <NavLink
          to="/ayuda"
          className={({ isActive }) =>
            `flex items-center gap-2.5 rounded-lg transition-colors ${isCollapsed ? 'justify-center h-9 w-9 mx-auto' : 'px-2.5 h-9'} ${
              isActive
                ? 'bg-white/[0.12] text-white'
                : 'text-white/50 hover:bg-white/[0.07] hover:text-white/80'
            }`
          }
          title="Ayuda"
          onClick={clearCrearSelection}
        >
          <span className="material-symbols-rounded text-[18px] shrink-0">help</span>
          {!isCollapsed && <span className="text-[13px] whitespace-nowrap overflow-hidden">Ayuda</span>}
        </NavLink>
        <NavLink
          to="/configuracion"
          className={({ isActive }) =>
            `flex items-center gap-2.5 rounded-lg transition-colors ${isCollapsed ? 'justify-center h-9 w-9 mx-auto' : 'px-2.5 h-9'} ${
              isActive
                ? 'bg-white/[0.12] text-white'
                : 'text-white/50 hover:bg-white/[0.07] hover:text-white/80'
            }`
          }
          title="Configuración"
          onClick={clearCrearSelection}
        >
          <span className="material-symbols-rounded text-[18px] shrink-0">settings</span>
          {!isCollapsed && <span className="text-[13px] whitespace-nowrap overflow-hidden">Configuración</span>}
        </NavLink>
      </nav>

      {/* ── Free trial upsell + User card at bottom ── */}
      <div className="shrink-0 px-3 pb-3">
        {!loading && userProfile?.plan === 'gratis' && !isCollapsed && (
          <div
            className="w-full mb-2 bg-brand-primary rounded-md px-3 py-2.5 text-left cursor-pointer hover:bg-brand-primary-hover transition-colors"
            onClick={() => navigate('/subscription')}
          >
            <div className="flex items-start gap-2.5">
              <Gift className="w-6 h-6 text-white/80 mt-0.5 shrink-0" />
              <p className="text-[13px] text-white/90 leading-snug">Te regalamos <span className="font-bold">{(userProfile?.pais || '').toLowerCase() === 'argentina' ? '30 imágenes gratis más.' : '50% de descuento en tu primer mes.'}</span></p>
            </div>
            <span className="block mt-2 text-[11px] font-semibold text-brand-primary bg-white rounded px-2 py-1 text-center w-full">Reclamar</span>
          </div>
        )}
        {loading ? (
          <div className="flex justify-center py-3">
            <Loader2 className="w-5 h-5 animate-spin text-white/40" />
          </div>
        ) : userProfile ? (
          <div className={`rounded-md bg-white/10 cursor-pointer hover:bg-white/[0.15] transition-colors ${isCollapsed ? 'p-2 flex justify-center' : 'px-4 py-3 flex items-center gap-3'}`} onClick={() => navigate('/configuracion')}>
            <img
              src={userProfile.company_logo ? `${userProfile.company_logo}?width=40&height=40` : "/images/estudio-atlas-logo-solo-blanco.svg"}
              alt="Logo"
              className="w-8 h-8 rounded-full object-cover bg-white/20 shrink-0"
              onError={(e) => {
                (e.target as HTMLImageElement).src = "/images/estudio-atlas-logo-solo-blanco.svg";
              }}
            />
            {!isCollapsed && (
              <>
                <div className="min-w-0 flex-1">
                  <div className="text-[length:var(--text-body)] font-medium text-white truncate">{userProfile.name || 'Usuario'}</div>
                  {userProfile.company_name && (
                    <div className="text-[length:var(--text-caption)] text-white/50 truncate">{userProfile.company_name}</div>
                  )}
                </div>
                {userProfile.plan && (
                  <span className="shrink-0 text-[11px] font-semibold leading-none px-1.5 py-0.5 rounded bg-white/20 text-white/80">
                    {PLAN_LABELS[userProfile.plan] || userProfile.plan}
                  </span>
                )}
              </>
            )}
          </div>
        ) : null}

        {/* ── Credits bar (desktop) ── */}
        {!loading && userProfile && !isCollapsed && (() => {
          const remaining = typeof userProfile.imagenes_restantes === 'number' ? userProfile.imagenes_restantes : 0;
          const max = typeof userProfile.max_fotos_mes === 'number' ? userProfile.max_fotos_mes : 0;
          const isFree = userProfile.plan === 'gratis';
          const pct = isFree
            ? (max > 0 ? (remaining / max) * 100 : 0)
            : (max > 0 ? (Math.min(remaining, max) / max) * 100 : 0);
          return (
            <div
              className="mt-2 px-1 cursor-pointer"
              onClick={() => navigate('/configuracion')}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] text-white/50">
                  {remaining} {isFree ? 'fotos gratuitas' : 'imágenes'} restantes
                </span>
              </div>
              <div className="h-[4px] bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-brand-primary transition-all duration-300"
                  style={{ width: `${Math.min(pct, 100)}%` }}
                />
              </div>
            </div>
          );
        })()}

        {/* ── Collapse toggle ── */}
        <button
          onClick={toggleCollapsed}
          className={`mt-1.5 flex items-center gap-2 rounded-lg transition-colors text-white/35 hover:text-white/60 hover:bg-white/[0.07] ${isCollapsed ? 'justify-center h-8 w-8 mx-auto' : 'px-2.5 h-8 w-full'}`}
          title={isCollapsed ? 'Expandir' : 'Contraer'}
        >
          {isCollapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
          {!isCollapsed && <span className="text-[13px]">Contraer</span>}
        </button>
      </div>
    </aside>
  </>
);
};